import React, { useEffect, useState } from 'react'
import Drawer from 'react-bottom-drawer'
import { Form } from 'react-bootstrap'
import cn from 'classnames'
import Button from 'react-bootstrap/Button'
import moment from 'moment'
import { getUserPreferences, saveUserPreferences } from '../../api/Users'
import {
  scheduleNotificationsWithPayload,
  stopScheduledNotifications
} from '../../helpers/Mobile'
import { useParams } from 'react-router-dom'
import {
  STATUS_COMPLETED,
  tryGetStatusForCourseObject
} from '../../api/CourseProgress'
import {
  EVENT_ACTION_CANCEL,
  EVENT_ACTION_PAGEVIEW,
  EVENT_ACTION_START,
  EVENT_CATEGORY_REMINDERS,
  saveUserEvent
} from '../../api/UserEvents'
import { KEY_PRACTICE_JOURNEYS } from 'components/common/api'

function ConfirmationDrawer({
  visible,
  fullCourse,
  courseProgress,
  handleDrawerVisible
}) {
  const defaultTime = moment().add(5, 'minutes').format('HH:mm')
  const [notificationTime, setNotificationTime] = useState(defaultTime)
  const [notificationsEnabled, setNotificationsEnabled] = useState(true)
  const [userPreferences, setUserPreferences] = useState()
  const [isLoaded, setIsLoaded] = useState(false)

  const { courseId } = useParams()
  const uncompletedLessons = fullCourse.lessons.filter(
    lesson =>
      tryGetStatusForCourseObject(courseProgress, lesson) !== STATUS_COMPLETED
  )

  const closeDrawer = () => handleDrawerVisible(false)

  // Utility function to clear out all existing notifications for journey
  const clearExistingNotifications = () => {
    try {
      const currentSettings = userPreferences?.notifications['journey']
      // Return if there are no existing notifications
      if (!currentSettings) return

      // Clear out any existing notifications for journey
      stopScheduledNotifications(JSON.stringify({ id: KEY_PRACTICE_JOURNEYS }))
    } catch (e) {
      console.error(e)
    }
  }

  // Utility function to create the notification depending on the uncompleted lessons length
  const generateAndScheduleNotificationPayloads = () => {
    // Prepare payload for event bridge
    const title = 'Your next session awaits!'
    const body =
      'Keeping up with daily sessions is key to making change. Your relationship will thank you!'
    let notificationPayload

    let messageArray = []

    for (let i = 1; i <= uncompletedLessons.length; i++) {
      messageArray.push({
        title,
        body,
        time: notificationTime
      })
    }

    // Create the payload for journey notifications
    notificationPayload = {
      key: KEY_PRACTICE_JOURNEYS,
      messages: messageArray
    }

    // Schedule the notification
    scheduleNotificationsWithPayload(notificationPayload)
  }

  /**
   * Event handler for when the user disables notifications for this practice
   */
  const handleDisableNotifications = () => {
    // Disable notifications
    const updatedPreferences = {
      ...userPreferences,
      notifications: {
        ...userPreferences?.notifications,
        journey: ''
      }
    }

    // Clear out any existing notifications for journey
    clearExistingNotifications()

    // Save the user's preferences
    saveUserPreferences(updatedPreferences)

    // Log the event
    saveUserEvent(EVENT_CATEGORY_REMINDERS, EVENT_ACTION_CANCEL, courseId)
  }

  /**
   * Handler function to save the user's notification preferences for a specific practice
   * Generates a payload to send to the event bridge to schedule notifications on the user's device
   */
  const handleSave = () => {
    // Prepare payload for event bridge
    const [hour, minute] = notificationTime.split(':')

    // Convert days and times to comma, colon and pipe separated strings
    const timeString = `${hour}:${minute}`

    // Some users may not have any notification preferences set yet
    const currentNotificationSettings = userPreferences?.notifications || {}

    const updatedPreferences = {
      ...userPreferences,
      notifications: {
        ...currentNotificationSettings,
        [KEY_PRACTICE_JOURNEYS]: timeString,
        push: true
      }
    }

    // Save the user's preferences
    saveUserPreferences(updatedPreferences)

    // Clear out any existing notifications for journey
    clearExistingNotifications()

    // Generate new notification payloads
    generateAndScheduleNotificationPayloads()

    // Close the drawer
    closeDrawer()

    // Log the event
    saveUserEvent(EVENT_CATEGORY_REMINDERS, EVENT_ACTION_START, courseId)
  }

  useEffect(() => {
    ;(async function getUserPreferencesAsync() {
      const preferences = await getUserPreferences()

      // Some users may not have notification preferences set yet
      try {
        const journeyNotification = preferences.notifications['journey']

        journeyNotification != null &&
          (journeyNotification === ''
            ? setNotificationTime(defaultTime)
            : setNotificationTime(journeyNotification))
      } catch (e) {
        setNotificationTime(defaultTime)
      }

      setUserPreferences(preferences)
      setIsLoaded(true)

      // Log access to this page
      saveUserEvent(EVENT_CATEGORY_REMINDERS, EVENT_ACTION_PAGEVIEW, courseId)
    })()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId])

  return (
    <Drawer
      duration={250}
      hideScrollbars={true}
      onClose={closeDrawer}
      isVisible={visible}
      className='reminder-drawer'
    >
      <Form.Group
        controlId='formEnableNotifications'
        className='form-group green-group visible mb-3'
      >
        <p>
          Remble will send a reminder for each session to help guide you along
          the way. You can change your settings in your profile at any time.
        </p>
        <Form.Check
          id='enableNotifications'
          size='lg'
          label={
            notificationsEnabled ? 'Reminders are on' : 'Turn On Reminders'
          }
          type='switch'
          onChange={e => {
            setNotificationsEnabled(e.currentTarget.checked)

            // Disable notifications for journey
            if (!e.currentTarget.checked) handleDisableNotifications()
          }}
          checked={notificationsEnabled}
          disabled={!isLoaded}
        />
      </Form.Group>
      <div className={cn({ 'd-none': !notificationsEnabled })}>
        <h1 className='mt-4 mb-3'>When would you like to be reminded?</h1>
        <Form.Group className='form-group gray-group'>
          <label htmlFor='startTime'>Choose time</label>
          <input
            type='time'
            pattern='[0-9]{2}:[0-9]{2}'
            id='startTime'
            name='startTime'
            defaultValue={notificationTime}
            onChange={e => setNotificationTime(e.currentTarget.value)}
          />
        </Form.Group>
        <div className='d-flex align-items-center justify-content-between mb-4 mt-5'>
          <button
            type='button'
            className='btn btn-outline-secondary w-100'
            onClick={closeDrawer}
          >
            Cancel
          </button>
          <Button
            className='w-100 ms-4'
            variant='primary'
            onClick={() => handleSave()}
          >
            Save
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export default ConfirmationDrawer
